"use client";

import { useState } from "react";
import { useRouter } from "next/navigation";
import {
  Briefcase,
  CalendarClock,
  CheckCircle2,
  CircleDashed,
  Fingerprint,
  KeyRound,
  Phone,
  Search,
  XCircle,
} from "lucide-react";

const COMPANY_SITE_URL = "https://bayerkashan.com";

type LookupResult = {
  applicant: { name: string | null; jobTitle: string };
  createdAt: string | null;
  interview: { status: string; label: string; date: string | null; time: string | null };
  document: { status: string; label: string; applicable: boolean };
  startWork: { status: string; label: string; applicable: boolean };
};

type Method = "code" | "phone";
type StageState = "done" | "current" | "rejected" | "pending";

function formatTrackingCodeInput(raw: string): string {
  const chars = raw
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, "")
    .slice(0, 12);
  return (chars.match(/.{1,4}/g) || [chars]).join("-");
}

function formatNationalCodeInput(raw: string): string {
  return raw.replace(/\D/g, "").slice(0, 10);
}

function formatMobileInput(raw: string): string {
  return raw.replace(/\D/g, "").slice(0, 11);
}

function stageBadgeClasses(state: StageState): string {
  switch (state) {
    case "done":
      return "border-emerald-600 bg-emerald-600 text-white";
    case "current":
      return "border-sky-600 bg-sky-600 text-white animate-pulse";
    case "rejected":
      return "border-rose-600 bg-rose-600 text-white";
    default:
      return "border-slate-700 bg-slate-900 text-slate-500";
  }
}

function StageIcon({ state }: { state: StageState }) {
  if (state === "done") return <CheckCircle2 className="h-4 w-4" />;
  if (state === "rejected") return <XCircle className="h-4 w-4" />;
  if (state === "current") return <CircleDashed className="h-4 w-4" />;
  return <CircleDashed className="h-4 w-4" />;
}

function StageTracker({ result }: { result: LookupResult }) {
  const interviewState: StageState =
    result.interview.status === "approved"
      ? "done"
      : result.interview.status === "rejected"
        ? "rejected"
        : "current";

  const documentState: StageState = !result.document.applicable
    ? "pending"
    : result.document.status === "approved"
      ? "done"
      : result.document.status === "needs_correction"
        ? "rejected"
        : "current";

  const startWorkState: StageState = !result.startWork.applicable
    ? "pending"
    : result.startWork.status === "started"
      ? "done"
      : "current";

  const stages: Array<{ key: string; title: string; label: string; state: StageState; extra?: string }> = [
    { key: "interview", title: "مصاحبه", label: result.interview.label, state: interviewState },
    { key: "document", title: "مدارک", label: result.document.applicable ? result.document.label : "—", state: documentState },
    { key: "startWork", title: "شروع به کار", label: result.startWork.applicable ? result.startWork.label : "—", state: startWorkState },
  ];

  return (
    <div className="flex items-start justify-between gap-1">
      {stages.map((stage, idx) => (
        <div key={stage.key} className="flex flex-1 items-start">
          <div className="flex flex-1 flex-col items-center gap-1.5 text-center">
            <span className={`flex h-8 w-8 items-center justify-center rounded-full border-2 ${stageBadgeClasses(stage.state)}`}>
              <StageIcon state={stage.state} />
            </span>
            <span className="text-[0.7rem] font-semibold text-slate-200">{stage.title}</span>
            <span className="text-[0.65rem] text-slate-400 leading-tight">{stage.label}</span>
          </div>
          {idx < stages.length - 1 && (
            <div className={`mt-4 h-0.5 flex-1 ${stage.state === "done" ? "bg-emerald-500" : "bg-slate-800"}`} />
          )}
        </div>
      ))}
    </div>
  );
}

function ResultCard({
  result,
  onGoToDocuments,
  documentsLoading,
  documentsError,
}: {
  result: LookupResult;
  onGoToDocuments: () => void;
  documentsLoading: boolean;
  documentsError: string | null;
}) {
  return (
    <div className="rounded-2xl border border-slate-800 bg-gradient-to-b from-slate-900 to-slate-950 p-5 space-y-4 text-xs md:text-sm">
      {(result.applicant.name || result.applicant.jobTitle) && (
        <div className="flex items-center gap-2 border-b border-slate-800 pb-3">
          <span className="flex h-9 w-9 items-center justify-center rounded-full bg-sky-500/15 text-sky-300">
            <Briefcase className="h-4 w-4" />
          </span>
          <div className="min-w-0">
            {result.applicant.name && <div className="truncate font-semibold text-slate-100">{result.applicant.name}</div>}
            {result.applicant.jobTitle && <div className="truncate text-[0.7rem] text-slate-400">{result.applicant.jobTitle}</div>}
          </div>
        </div>
      )}

      <StageTracker result={result} />

      {result.interview.date && result.interview.time && (
        <div className="flex items-center justify-center gap-2 rounded-xl border border-sky-800/50 bg-sky-950/30 px-3 py-2 text-[0.75rem] text-sky-200">
          <CalendarClock className="h-4 w-4" />
          زمان مصاحبه: {result.interview.date} – {result.interview.time}
        </div>
      )}

      {result.interview.status === "approved" && (
        <div className="space-y-2">
          <button
            type="button"
            onClick={onGoToDocuments}
            disabled={documentsLoading}
            className="block w-full rounded-xl bg-sky-600 px-4 py-2.5 text-center text-xs font-bold text-white hover:bg-sky-500 transition-colors disabled:opacity-60"
          >
            {documentsLoading ? "در حال ورود..." : "تکمیل مدارک استخدام"}
          </button>
          {documentsError && (
            <div className="rounded-xl border border-rose-700/60 bg-rose-950/40 px-3 py-2 text-xs text-rose-200">{documentsError}</div>
          )}
        </div>
      )}
    </div>
  );
}

export default function EmploymentTrackingClient() {
  const router = useRouter();
  const [method, setMethod] = useState<Method>("code");

  const [trackingCode, setTrackingCode] = useState("");
  const [nationalCode, setNationalCode] = useState("");

  const [mobilePhone, setMobilePhone] = useState("");
  const [nationalCode2, setNationalCode2] = useState("");

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<LookupResult | null>(null);

  const [documentsLoading, setDocumentsLoading] = useState(false);
  const [documentsError, setDocumentsError] = useState<string | null>(null);

  // متقاضی همین الان با کد رهگیری/کدملی یا موبایل/کدملی هویتش را در همین صفحه
  // ثابت کرده، پس نباید در صفحه‌ی «تکمیل مدارک» دوباره همان اطلاعات را وارد
  // کند — همان‌جا (بر همان مبنایی که وارد کرده) یک نشست واقعی می‌گیریم و
  // مستقیم با توکن وارد صفحه‌ی آپلود می‌شویم.
  const handleGoToDocuments = async () => {
    setDocumentsLoading(true);
    setDocumentsError(null);
    try {
      const endpoint =
        method === "code" ? "/api/employment-documents/session" : "/api/employment-documents/session-by-phone";
      const payload =
        method === "code" ? { trackingCode, nationalCode } : { mobilePhone, nationalCode: nationalCode2 };
      const res = await fetch(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const data = await res.json().catch(() => null);
      if (!res.ok || !data?.success || !data?.token) {
        setDocumentsError(data?.message || "ورود به صفحه مدارک انجام نشد.");
        setDocumentsLoading(false);
        return;
      }
      const params = new URLSearchParams({ token: data.token });
      if (data.applicantName) params.set("name", data.applicantName);
      router.push("/employment-documents?" + params.toString());
    } catch {
      setDocumentsError("خطا در ارتباط با سرور. دوباره تلاش کنید.");
      setDocumentsLoading(false);
    }
  };

  const switchMethod = (m: Method) => {
    setMethod(m);
    setError(null);
    setResult(null);
  };

  const handleSubmitByCode = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    setResult(null);

    try {
      const res = await fetch("/api/employment-tracking/lookup", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ trackingCode, nationalCode }),
      });
      const data = await res.json().catch(() => null);
      if (!res.ok || !data?.success) {
        setError(data?.message || "خطا در دریافت اطلاعات.");
        setSubmitting(false);
        return;
      }
      setResult(data);
      setSubmitting(false);
    } catch {
      setError("خطا در ارتباط با سرور. دوباره تلاش کنید.");
      setSubmitting(false);
    }
  };

  const handleSubmitByPhone = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    setResult(null);

    try {
      const res = await fetch("/api/employment-tracking/lookup-by-phone", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ mobilePhone, nationalCode: nationalCode2 }),
      });
      const data = await res.json().catch(() => null);
      if (!res.ok || !data?.success) {
        setError(data?.message || "خطا در دریافت اطلاعات.");
        setSubmitting(false);
        return;
      }
      setResult(data);
      setSubmitting(false);
    } catch {
      setError("خطا در ارتباط با سرور. دوباره تلاش کنید.");
      setSubmitting(false);
    }
  };

  return (
    <main className="min-h-screen bg-slate-950 text-slate-50" dir="rtl">
      <div className="relative overflow-hidden">
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_20%_-10%,rgb(var(--t-sky,var(--c-sky-500))/calc(0.18*var(--tint-k-500,1))),transparent_45%),radial-gradient(circle_at_85%_10%,rgb(var(--t-emerald,var(--c-emerald-500))/calc(0.12*var(--tint-k-500,1))),transparent_40%)]" />

        <div className="relative mx-auto flex min-h-screen w-full max-w-5xl flex-col px-4 py-8 md:py-12">
          <a
            href={COMPANY_SITE_URL}
            className="mb-6 inline-flex w-fit items-center gap-2 text-xs md:text-sm text-slate-300 hover:text-slate-100 transition-colors"
          >
            <span aria-hidden="true">↩</span>
            <span>بازگشت به سایت بایر کاشان</span>
          </a>

          <div className="grid flex-1 grid-cols-1 items-center gap-8 md:grid-cols-2">
            <div className="hidden md:flex flex-col gap-4 pl-4">
              <span className="w-fit rounded-full border border-sky-700/50 bg-sky-950/40 px-3 py-1 text-xs font-semibold text-sky-300">
                واحد منابع انسانی — بایر کاشان
              </span>
              <h1 className="text-3xl font-extrabold leading-tight text-slate-50">
                رهگیری درخواست
                <br />
                استخدام شما
              </h1>
              <p className="max-w-sm text-sm leading-relaxed text-slate-400">
                وضعیت مصاحبه، مدارک و شروع به کار خود را در هر لحظه، فقط با کد رهگیری یا شماره موبایل، مشاهده کنید.
              </p>
              <div className="mt-2 flex items-center gap-4 text-xs text-slate-500">
                <div className="flex items-center gap-1.5">
                  <CheckCircle2 className="h-4 w-4 text-emerald-400" />
                  اطلاعات لحظه‌ای
                </div>
                <div className="flex items-center gap-1.5">
                  <Fingerprint className="h-4 w-4 text-sky-400" />
                  محرمانه و امن
                </div>
              </div>
            </div>

            <div className="mx-auto w-full max-w-md rounded-3xl border border-slate-800 bg-slate-900/80 shadow-[0_24px_80px_rgb(var(--shadow-ink)/calc(0.8*var(--shadow-k)))] backdrop-blur-xl p-6 md:p-8 space-y-5">
              <header className="space-y-1 text-center md:hidden">
                <p className="text-xs text-sky-400 font-semibold tracking-wide">واحد منابع انسانی — بایر کاشان</p>
                <h1 className="text-xl font-extrabold text-slate-50">رهگیری درخواست استخدام</h1>
              </header>

              <div className="grid grid-cols-2 gap-2 rounded-xl border border-slate-800 bg-slate-950/60 p-1 text-xs">
                <button
                  type="button"
                  onClick={() => switchMethod("code")}
                  className={`flex items-center justify-center gap-1.5 rounded-lg py-2 font-semibold transition-colors ${
                    method === "code" ? "bg-sky-600 text-white" : "text-slate-300 hover:text-slate-100"
                  }`}
                >
                  <KeyRound className="h-3.5 w-3.5" />
                  کد رهگیری
                </button>
                <button
                  type="button"
                  onClick={() => switchMethod("phone")}
                  className={`flex items-center justify-center gap-1.5 rounded-lg py-2 font-semibold transition-colors ${
                    method === "phone" ? "bg-sky-600 text-white" : "text-slate-300 hover:text-slate-100"
                  }`}
                >
                  <Phone className="h-3.5 w-3.5" />
                  شماره موبایل
                </button>
              </div>

              {method === "code" ? (
                <form onSubmit={handleSubmitByCode} className="space-y-3">
                  <p className="text-xs text-slate-400">
                    کد رهگیری‌ای که هنگام ثبت فرم دریافت کرده‌اید را همراه با کد ملی وارد کنید.
                  </p>
                  <div className="space-y-1.5">
                    <label className="block text-xs text-slate-300">کد رهگیری</label>
                    <div className="relative">
                      <KeyRound className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
                      <input
                        type="text"
                        required
                        value={trackingCode}
                        onChange={(e) => setTrackingCode(formatTrackingCodeInput(e.target.value))}
                        placeholder="XXXX-XXXX-XXXX"
                        className="w-full rounded-xl border border-slate-700 bg-slate-950 py-2 pl-3 pr-9 text-sm text-slate-100 ltr text-left tracking-widest focus:outline-none focus:ring-1 focus:ring-sky-500"
                      />
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <label className="block text-xs text-slate-300">کد ملی</label>
                    <div className="relative">
                      <Fingerprint className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
                      <input
                        type="text"
                        required
                        inputMode="numeric"
                        value={nationalCode}
                        onChange={(e) => setNationalCode(formatNationalCodeInput(e.target.value))}
                        placeholder="0012345678"
                        className="w-full rounded-xl border border-slate-700 bg-slate-950 py-2 pl-3 pr-9 text-sm text-slate-100 ltr text-left focus:outline-none focus:ring-1 focus:ring-sky-500"
                      />
                    </div>
                  </div>

                  {error && (
                    <div className="rounded-xl border border-rose-700/60 bg-rose-950/40 px-3 py-2 text-xs text-rose-200">{error}</div>
                  )}

                  <button
                    type="submit"
                    disabled={submitting}
                    className="flex w-full items-center justify-center gap-1.5 rounded-xl px-5 py-2.5 text-sm font-bold bg-sky-600 text-white hover:bg-sky-500 transition-colors disabled:opacity-60"
                  >
                    <Search className="h-4 w-4" />
                    {submitting ? "در حال بررسی..." : "مشاهده وضعیت"}
                  </button>
                </form>
              ) : (
                <form onSubmit={handleSubmitByPhone} className="space-y-3">
                  <p className="text-xs text-slate-400">
                    اگر کد رهگیری را گم کرده‌اید، با شماره موبایل و کد ملی‌ای که هنگام ثبت فرم وارد کرده بودید امتحان کنید.
                  </p>
                  <div className="space-y-1.5">
                    <label className="block text-xs text-slate-300">شماره موبایل</label>
                    <div className="relative">
                      <Phone className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
                      <input
                        type="text"
                        required
                        inputMode="numeric"
                        value={mobilePhone}
                        onChange={(e) => setMobilePhone(formatMobileInput(e.target.value))}
                        placeholder="09121234567"
                        className="w-full rounded-xl border border-slate-700 bg-slate-950 py-2 pl-3 pr-9 text-sm text-slate-100 ltr text-left focus:outline-none focus:ring-1 focus:ring-sky-500"
                      />
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <label className="block text-xs text-slate-300">کد ملی</label>
                    <div className="relative">
                      <Fingerprint className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
                      <input
                        type="text"
                        required
                        inputMode="numeric"
                        value={nationalCode2}
                        onChange={(e) => setNationalCode2(formatNationalCodeInput(e.target.value))}
                        placeholder="0012345678"
                        className="w-full rounded-xl border border-slate-700 bg-slate-950 py-2 pl-3 pr-9 text-sm text-slate-100 ltr text-left focus:outline-none focus:ring-1 focus:ring-sky-500"
                      />
                    </div>
                  </div>

                  {error && (
                    <div className="rounded-xl border border-rose-700/60 bg-rose-950/40 px-3 py-2 text-xs text-rose-200">{error}</div>
                  )}

                  <button
                    type="submit"
                    disabled={submitting}
                    className="flex w-full items-center justify-center gap-1.5 rounded-xl px-5 py-2.5 text-sm font-bold bg-sky-600 text-white hover:bg-sky-500 transition-colors disabled:opacity-60"
                  >
                    <Search className="h-4 w-4" />
                    {submitting ? "در حال بررسی..." : "مشاهده وضعیت"}
                  </button>
                </form>
              )}

              {result && (
                <ResultCard
                  result={result}
                  onGoToDocuments={handleGoToDocuments}
                  documentsLoading={documentsLoading}
                  documentsError={documentsError}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </main>
  );
}
